import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { MagnifyingGlass, Bell, Moon, Sun, UserPlus, CurrencyInr, ArrowsClockwise, Warning, Info, Check } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import api from '@/lib/api';

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

const NOTIFICATION_ICONS = {
  lead_assigned: UserPlus,
  payment_received: CurrencyInr,
  stage_changed: ArrowsClockwise,
  follow_up_overdue: Warning,
  system: Info,
  info: Info,
};

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/api/notifications?limit=30');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    }
    if (notifOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [notifOpen]);

  const markRead = async (notifId) => {
    try {
      await api.put(`/api/notifications/${notifId}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n._id === notifId ? { ...n, read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // Silently fail
    }
  };

  const markAllRead = async () => {
    try {
      await api.put('/api/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) {
      markRead(notif._id);
    }
    if (notif.link) {
      navigate(notif.link);
    }
    setNotifOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      <div className={`transition-all duration-300 ${collapsed ? 'ml-[64px]' : 'ml-[232px]'}`}>
        {/* Glass Header */}
        <header
          className="glass-header sticky top-0 z-30 h-[60px] border-b border-border flex items-center justify-between px-6"
          style={{ backgroundColor: theme === 'dark' ? 'rgba(15,15,15,0.8)' : 'rgba(247,246,243,0.8)' }}
          data-testid="top-header"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search leads, students..."
                data-testid="global-search"
                className="pl-9 pr-4 py-2 text-[13px] bg-card border border-border rounded-lg w-72 text-foreground placeholder:text-muted-foreground/50 transition-all focus:w-80"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? <Sun size={17} weight="bold" /> : <Moon size={17} weight="bold" />}
            </button>
            {/* Notification Bell */}
            <div className="relative" ref={dropdownRef}>
              <button
                data-testid="notifications-btn"
                className="relative p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setNotifOpen((v) => !v);
                  if (!notifOpen) fetchNotifications();
                }}
              >
                <Bell size={17} />
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1 font-mono"
                    style={{ backgroundColor: '#E04B2A' }}
                    data-testid="notifications-badge"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-[380px] bg-card border border-border rounded-sm z-50 overflow-hidden"
                  data-testid="notifications-dropdown"
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <span className="text-[13px] font-semibold text-foreground">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors font-mono"
                        data-testid="mark-all-read-btn"
                      >
                        <Check size={12} />
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-[12px] text-muted-foreground">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.map((notif) => {
                        const IconComp = NOTIFICATION_ICONS[notif.type] || Info;
                        return (
                          <button
                            key={notif._id}
                            onClick={() => handleNotifClick(notif)}
                            className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-secondary/50 transition-colors flex items-start gap-3 ${
                              !notif.read ? 'bg-secondary/30' : ''
                            }`}
                            data-testid={`notification-item-${notif._id}`}
                          >
                            <div className="mt-0.5 flex-shrink-0">
                              <IconComp size={16} className={!notif.read ? 'text-foreground' : 'text-muted-foreground'} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-[12px] font-semibold leading-tight ${!notif.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                                  {notif.title}
                                </span>
                                {!notif.read && (
                                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#2563EB' }} />
                                )}
                              </div>
                              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                                {notif.message}
                              </p>
                              <span className="text-[10px] text-muted-foreground/60 font-mono mt-1 block">
                                {timeAgo(notif.created_at)}
                              </span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="w-px h-6 bg-border mx-1" />
            <div className="flex items-center gap-2.5">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold text-white font-display"
                style={{ backgroundColor: '#E04B2A' }}
              >
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="hidden sm:block">
                <p className="text-[13px] font-semibold text-foreground leading-none">{user?.name}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5 capitalize font-mono">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </header>
        {/* Page Content */}
        <main className="p-7 max-w-[1440px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
